import _ from "lodash"
import React, { useContext, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { handleGqlError } from "../../../components/utils"
import {
  PaperStatus,
  useDinoLoadPaperLazyQuery,
  useDinoPaperQuery,
  useDinoProcessResponseMutation,
} from "../../../generated-types"
import { ProcessingPage } from "./ProcessingPage"
import { useMachine } from "@xstate/react"
import { useTranslation } from "react-i18next"
import { ToastsStore } from "react-toasts"
import { useLoginContext } from "../../../components/context/LoginContext"
import { dinoMachine } from "../machines/dinoMachine"

type Response = { [value: string]: number | null }
type DinoContextType = {
  page: number
  waiting: boolean
  isLast: boolean
  meta: any[]
  nav: [boolean, boolean] // 네비게이션 가능 여부 - 이전, 다음
  response: Response

  putResponse: (response: Response) => void
  goNext: (waitBeforeNext?: boolean) => void
  goPrev: () => void
  spliceSheets: (contentId: number) => void
}

export interface DinoContextProviderProps {
  paperId: string
  lang: string
  children: React.ReactNode
}

export const DinoContext = React.createContext<DinoContextType | undefined>(undefined)
DinoContext.displayName = "DinoContext"

export const DinoContextProvider: React.FC<DinoContextProviderProps> = ({ paperId, lang, children }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { isLogined } = useLoginContext()
  const loggedIn = isLogined()
  const [submitDone, setSubmitDone] = useState(false)
  const { data: paperData, error: paperError } = useDinoPaperQuery({
    variables: { paperId },
    context: { "x-dino-lang": lang },
    fetchPolicy: "network-only",
    skip: !loggedIn,
  })

  // The login token lives in sessionStorage, so it is gone when the page is
  // reopened in a new tab. Send the user back to enter their code instead of
  // leaving them on "Loading..." forever.
  useEffect(() => {
    if (!loggedIn) {
      ToastsStore.error(t("error.no-auth", "No authentication info"))
      navigate("/", { replace: true })
    }
  }, [loggedIn])

  useEffect(() => {
    if (paperError) {
      handleGqlError(paperError)
      navigate("/my", { replace: true })
    }
  }, [paperError])
  const [fetchPaperSheet, { data, error: sheetError }] = useDinoLoadPaperLazyQuery({
    variables: { paperId, lang },
    fetchPolicy: "network-only",
  })

  useEffect(() => {
    if (sheetError) {
      handleGqlError(sheetError)
      navigate("/my", { replace: true })
    }
  }, [sheetError])
  const [submitResponse] = useDinoProcessResponseMutation({ context: { "x-dino-lang": lang } })
  const [snapshot, send] = useMachine(dinoMachine)

  // Paper already done → navigate to result; otherwise fetch sheets
  useEffect(() => {
    if (!paperData) return
    if (paperData.dinoPaper.statusCd === PaperStatus.Done) {
      navigate(`/papers/${paperId}/${paperData.dinoPaper.item.id}/result`, { replace: true })
    } else if (snapshot.matches("loading")) {
      fetchPaperSheet()
    }
  }, [paperData])

  // Build sheets from Apollo data and send to machine
  useEffect(() => {
    if (!data) return
    const sheets: any[][] = []
    data.dinoLoadPaper.content.forEach(({ intro, items }, cid) => {
      if (intro) {
        sheets.push([`/${cid}/intro`])
      }
      const itemCnt = items.length
      items.forEach((_, i) => {
        const itemNo = i + 1
        sheets.push([`/${cid}/${itemNo}`, itemNo, itemCnt])
      })
    })
    sheets.push(["FINISHED"])
    send({ type: "SHEETS_LOADED", sheets, contentCount: data.dinoLoadPaper.content.length })
  }, [data])

  // Navigate when page changes in active.idle
  const currentPage = snapshot.context.page
  const isIdle = snapshot.matches({ active: "idle" })
  useEffect(() => {
    if (!paperData || !isIdle) return
    const { sheets } = snapshot.context
    if (!sheets[currentPage]) return
    const path = sheets[currentPage][0]
    if (path === "FINISHED") return
    navigate(`/papers/${paperId}/${paperData.dinoPaper.item.id}${path}`, { replace: true })
  }, [currentPage, isIdle])

  // Submit mutation when machine reaches processing
  const isProcessingState = snapshot.matches("processing")
  useEffect(() => {
    if (!isProcessingState || !paperData) return
    const { allResponse } = snapshot.context
    const answers = _(allResponse)
      .compact()
      .map((v) => {
        const arr: string[] = []
        Object.entries(v).forEach(([value, score]) => {
          if (score !== null) arr[score] = value
        })
        return _(arr).compact().join(",").valueOf()
      })
      .value()
    submitResponse({ variables: { paperId, answers } })
      .then(() => {
        setSubmitDone(true)
        const itemId = paperData.dinoPaper.item.id
        navigate(`/papers/${paperId}/${itemId}/result`, { replace: true })
      })
      .catch((e) => {
        console.error("Submit failed:", e)
        handleGqlError(e)
      })
  }, [isProcessingState])

  // Build context value for consumers (API unchanged)
  const ctx = useMemo<DinoContextType | undefined>(() => {
    if (!data || !paperData) return undefined
    const { page, allResponse, sheets } = snapshot.context
    if (sheets.length === 0) return undefined
    const isLast = page >= sheets.length - 1
    return {
      page,
      waiting: snapshot.matches({ active: "waiting" }),
      isLast,
      meta: sheets[page],
      nav: [page !== 0, !!allResponse[page]] as [boolean, boolean],
      response: allResponse[page] || {},
      putResponse: (response: Response) => send({ type: "PUT_RESPONSE", response }),
      goNext: (waitBeforeNext?: boolean) => send({ type: "GO_NEXT", wait: waitBeforeNext }),
      goPrev: () => send({ type: "GO_PREV" }),
      spliceSheets: (contentId: number) => send({ type: "SPLICE_SHEETS", contentId }),
    }
  }, [data, paperData, snapshot])

  const showProcessing = !submitDone && (snapshot.matches("submitting") || snapshot.matches("processing"))
  return <DinoContext.Provider value={ctx}>{showProcessing ? <ProcessingPage /> : children}</DinoContext.Provider>
}

export const useDinoContext = () => useContext(DinoContext)
